<script lang="ts" setup>
import { GpAvatar, GpBtn, GpTable, GpTableColumn } from '@gportal/uikit';
import ClientDetailModal from '@panel/components/user/clients/ClientDetailModal.vue';
import ClientSecretModal from '@panel/components/user/clients/ClientSecretModal.vue';
import CreateClientModal from '@panel/components/user/clients/CreateClientModal.vue';
import useGpTableData from '@panel/composables/useGpTableData';
import SettingsLayout from '@panel/layouts/Layout.User.Settings.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import { type Client } from 'gpcore-grpc/gpcore/api/auth/v1/client_pb';
import type {
  ListClientsResponse,
  ResetClientSecretResponse,
} from 'gpcore-grpc/gpcore/api/auth/v1/responses_pb';
import type { BasicUser } from 'gpcore-grpc/gpcore/api/cloud/v1/user_pb';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import { onMounted, reactive, ref } from 'vue';
import useDateFormat from 'gpcore-uikit/composables/useDateFormat';
import useUserViewTitle from '@panel/composables/useUserViewTitle';
import useNotifications from 'gpcore-uikit/composables/useNotifications';

useUserViewTitle('OAuth clients');

type ClientsTableRow = {
  key: string;
  name: Client;
  serviceAccount: BasicUser;
  createdAt: string;
};

const userStore = useUserStore();
const { addNotification } = useNotifications();

const clients = ref<Array<Client>>([]);

// data
const reloadClients = () => {
  userStore
    .listClients()
    .then((resp: ListClientsResponse) => {
      clients.value = resp.clients;
    })
    .catch((error: ServiceError) => {
      addNotification({
        message: error.message,
        type: 'danger',
      });
    });
};

const format = useDateFormat();
const { tableData } = useGpTableData(clients, (data) =>
  data.map((client) => ({
    key: client.id,
    name: client,
    serviceAccount: client.serviceAccount,
    createdAt: format(client.createdAt),
  })),
);

// secret modal
const clientSecret = reactive({
  showModal: false,
  clientId: '',
  clientSecret: '',
});
const handleSecretModalClose = async () => {
  clientSecret.showModal = false;
  clientSecret.clientId = undefined;
  clientSecret.clientSecret = undefined;
};

// create client
const createModal = ref({
  showModal: false,
});
const handleClientCreated = (client: Client) => {
  createModal.value = { showModal: false };
  reloadClients();

  // let the user see the secret
  clientSecret.clientId = client.id;
  clientSecret.clientSecret = client.secret;
  clientSecret.showModal = true;
};

// reset secret
const isResetting = ref(false);
const resetSecretModal = ref({ show: false, id: undefined });
const clickedResetSecret = (id: string) => {
  resetSecretModal.value = {
    id,
    show: true,
  };
};
const resetSecret = () => {
  isResetting.value = true;
  userStore
    .resetClientSecret(resetSecretModal.value.id)
    .then(async (resp: ResetClientSecretResponse) => {
      clientSecret.showModal = true;
      clientSecret.clientId = resetSecretModal.value.id;
      clientSecret.clientSecret = resp.secret;

      resetSecretModal.value.show = false;
      resetSecretModal.value.id = '';
      reloadClients();
    })
    .catch((error: ServiceError) => {
      addNotification({
        message: error.message,
        type: 'danger',
      });
    })
    .finally(() => {
      isResetting.value = false;
    });
};

// delete client
const deleteModal = ref({ show: false, id: undefined });
const isDeleting = ref(false);
const clickedDelete = (id: string) => {
  deleteModal.value = {
    id,
    show: true,
  };
};
const deleteClient = () => {
  isDeleting.value = true;
  userStore
    .deleteClient(deleteModal.value.id)
    .then(() => {
      reloadClients();
      deleteModal.value.show = false;
    })
    .catch((error: ServiceError) => {
      addNotification({
        message: error.message,
        type: 'danger',
      });
    })
    .finally(() => {
      isDeleting.value = false;
    });
};

// client detail modal
const detailModal = ref<{ showModal: boolean; client: Client }>({
  showModal: false,
  client: undefined,
});
const openClientDetail = async (client: Client) => {
  detailModal.value = { showModal: true, client };
};
const handleClientDetailClose = () => {
  detailModal.value = { showModal: false, client: undefined };
};

const handleClientUpdated = () => {
  detailModal.value = { showModal: false, client: undefined };
  reloadClients();
};

onMounted(() => {
  reloadClients();
});
</script>

<template>
  <SettingsLayout v-playwright="'oauth-clients-view'">
    <template #buttons>
      <GpBtn
        size="sm"
        label="Create"
        :icon="['fas', 'fa-plus']"
        v-playwright="'add-oauth-client-btn'"
        @click="createModal.showModal = true"
      />
    </template>
    <template #content>
      <ConfirmModal
        :show="deleteModal.show"
        confirm-text="Delete"
        confirm-type="danger"
        :loading="isDeleting"
        @close="deleteModal.show = false"
        @confirm="deleteClient"
      />
      <ConfirmModal
        :show="resetSecretModal.show"
        confirm-text="Regenerate secret"
        confirm-type="warning"
        confirm-icon="retweet"
        :loading="isResetting"
        @close="resetSecretModal.show = false"
        @confirm="resetSecret"
      />
      <CreateClientModal
        :show="createModal.showModal"
        @close="createModal.showModal = false"
        @created="handleClientCreated"
      />
      <ClientDetailModal
        :show="detailModal.showModal"
        :client="detailModal.client"
        @close="handleClientDetailClose"
        @updated="handleClientUpdated"
      />
      <ClientSecretModal
        :show="clientSecret.showModal"
        :client-secret="clientSecret.clientSecret"
        :client-id="clientSecret.clientId"
        @close="handleSecretModalClose"
      />

      <GpTable
        :data="tableData"
        empty-message="No clients provided"
        v-playwright="'oauth-clients-table'"
      >
        <GpTableColumn field="name" label="Name">
          <template #body="{ data: name }: { data: ClientsTableRow['name'] }">
            <a type="button" @click="openClientDetail(name)">
              {{ name.name }}
            </a>
          </template>
        </GpTableColumn>

        <GpTableColumn field="serviceAccount" label="Service account">
          <template #body="{ data: serviceAccount }: { data: ClientsTableRow['serviceAccount'] }">
            <div class="tw-flex tw-flex-wrap tw-items-center tw-leading-none">
              <GpAvatar
                class="tw-mr-4"
                :src="serviceAccount.avatarUrl"
                :alt="serviceAccount.username"
                rounded
                custom-size
                style="--gp-avatar-size: 32px"
              />

              <div>
                {{ serviceAccount.username }}<br />
                <small>User ID: {{ serviceAccount.id }}</small>
              </div>
            </div>
          </template>
        </GpTableColumn>

        <GpTableColumn field="createdAt" label="Created" />

        <GpTableColumn field="actions" label="">
          <template #body="{ row }: { row: ClientsTableRow }">
            <div class="tw-flex tw-justify-end tw-gap-4">
              <GpBtn
                btn-type="warning"
                size="sm"
                :icon="['fas', 'fa-retweet']"
                label="Recreate Secret"
                @click="clickedResetSecret(row.key)"
              />
              <GpBtn
                btn-type="danger"
                size="sm"
                :icon="['fas', 'fa-trash']"
                label="Delete"
                @click="clickedDelete(row.key)"
              />
            </div>
          </template>
        </GpTableColumn>
      </GpTable>
    </template>
  </SettingsLayout>
</template>
